import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

const GREETING = "Namaste! Ask me about any product — immersion rods, cylinders, contactors, wires… English या हिंदी, दोनों चलेंगे।";

function getSessionId() {
  let sid = localStorage.getItem("hotc_chat_session");
  if (!sid) {
    sid = `s-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    localStorage.setItem("hotc_chat_session", sid);
  }
  return sid;
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", text: GREETING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${api.defaults.baseURL}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const part of parts) {
          if (!part.startsWith("data: ") || part === "data: [DONE]") continue;
          try {
            const payload = JSON.parse(part.slice(6));
            if (payload.error) {
              setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", text: payload.error }; return c; });
            } else if (payload.t) {
              setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", text: c[c.length - 1].text + payload.t }; return c; });
            }
          } catch { /* partial chunk */ }
        }
      }
    } catch {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", text: "Connection issue — please call +91 82792 21172 or WhatsApp us." }; return c; });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        data-testid="ai-chat-button"
        aria-label={open ? "Close chat assistant" : "Open chat assistant"}
        className="fixed bottom-24 right-4 z-50 flex h-14 w-14 items-center justify-center rounded-full border-2 border-[#F5B700] bg-[#111111] text-[#F5B700] shadow-[0_8px_30px_rgba(0,0,0,0.35)] transition-transform hover:scale-105 sm:bottom-6 sm:right-6"
      >
        {open ? <X size={22} aria-hidden="true" /> : <MessageCircle size={22} aria-hidden="true" />}
      </button>

      {open && (
        <div
          data-testid="ai-chat-panel"
          className="fixed bottom-40 right-4 z-50 flex h-[60vh] max-h-[520px] w-[calc(100vw-2rem)] max-w-sm flex-col border-2 border-[#111111] bg-white shadow-[0_20px_60px_rgba(0,0,0,0.35)] sm:bottom-24 sm:right-6"
        >
          <div className="flex items-center gap-2.5 bg-[#111111] px-4 py-3">
            <Sparkles size={16} className="text-[#F5B700]" aria-hidden="true" />
            <div>
              <p className="font-heading text-sm font-bold text-white">Hari Om AI Assistant</p>
              <p className="text-[11px] text-[#98a2b3]">Products, sizes, availability — English / हिंदी</p>
            </div>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto bg-[#F6F7F8] p-3" data-testid="ai-chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] whitespace-pre-wrap px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === "user" ? "bg-[#111111] text-white" : "border border-[#E4E7EC] bg-white text-[#161616]"
                  }`}
                >
                  {m.text || (busy && i === messages.length - 1 ? <Loader2 size={15} className="animate-spin text-[#F5B700]" aria-hidden="true" /> : null)}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <div className="flex gap-2 border-t border-[#E4E7EC] bg-white p-3">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Type your question…"
              data-testid="ai-chat-input"
              className="min-h-[44px] flex-1 border border-[#D9DDE3] px-3.5 text-sm focus:border-[#F5B700] focus:outline-none"
            />
            <button
              onClick={send}
              disabled={busy || !input.trim()}
              data-testid="ai-chat-send"
              aria-label="Send message"
              className="flex min-h-[44px] w-12 items-center justify-center bg-[#F5B700] text-[#111111] transition-colors hover:bg-[#111111] hover:text-[#F5B700] disabled:opacity-50"
            >
              <Send size={17} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
